import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { CalendarGrid } from "../components/booking-calendar";
import { normalizeLocale } from "../lib/locale";
import { PW, pewterInset, pewterPanel } from "./pewter-shell";
import { measure, type Anchor } from "./panel-placement";

// Kept exported from here: the picker tests and older imports read it from this module.
export { placePanel, type PlacementInput } from "./panel-placement";

/**
 * The pewter pickers: a dropdown, a date field and a time field that never
 * open a browser-native popup (owner rule: no grey macOS / Chrome pickers on
 * the site).
 *
 * Every panel is portalled to <body> and positioned `fixed` off its trigger.
 * The booking surfaces sit inside scroll containers, and an absolutely
 * positioned panel is clipped by its scroll container whatever its z-index
 * (the phone country list lost 214 of 216 rows that way). Below 640px the
 * panel comes up from the bottom as a sheet instead, with a scrim and a close
 * button, so a thumb can reach every row.
 *
 * Triggers wear `.mj-pw-field`, which `BookingSkinScope skin="pewter"` injects,
 * so a caller must render these inside that scope.
 */

const NARROW_QUERY = "(max-width: 639px)";
const Z = 2147483000;

/* ------------------------------------------------------------------ */
/* Popover plumbing                                                    */
/* ------------------------------------------------------------------ */

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW_QUERY);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  return narrow;
}

function usePopover() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  /** Closes, and by default hands focus back to the trigger. */
  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    /* The panel is outside the trigger's subtree (it is portalled), so both
       have to be exempted or the first press on a row closes the panel
       instead of choosing the row. A press elsewhere leaves focus where the
       browser puts it. */
    function onDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    /* Capture phase, and stopped: Escape closes this panel and never reaches
       a sheet or modal the form may be sitting in. */
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
    }
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, close]);

  return { open, setOpen, close, triggerRef, panelRef };
}

const FOCUSABLE = 'button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled])';

interface PanelProps {
  readonly open: boolean;
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly onClose: (refocus?: boolean) => void;
  readonly title: string;
  readonly labelledBy: string;
  readonly role: "dialog" | "listbox";
  /** A number of px, or the trigger's own width. */
  readonly width: number | "trigger";
  readonly estHeight: number;
  /** The element to focus once the panel lands; defaults to the first control. */
  readonly initialFocus?: () => HTMLElement | null | undefined;
  /** A listbox closes on Tab, as a native select does; a dialog keeps focus inside. */
  readonly tabCloses?: boolean;
  readonly children: ReactNode;
}

function PopoverPanel({
  open,
  triggerRef,
  panelRef,
  onClose,
  title,
  labelledBy,
  role,
  width,
  estHeight,
  initialFocus,
  tabCloses = false,
  children,
}: PanelProps) {
  const { t } = useTranslation();
  const narrow = useNarrow();
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  /* Measured on open, then kept glued to the trigger through any scroll
     (capture: inner containers' scrolls do not bubble) and any resize. */
  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const place = (e?: Event) => {
      // A column scrolling inside the panel does not move the trigger.
      if (e?.target instanceof Node && panelRef.current?.contains(e.target)) return;
      if (triggerRef.current) setAnchor(measure(triggerRef.current, width, estHeight));
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, triggerRef, panelRef, width, estHeight]);

  const ready = open && anchor !== null;
  const focusRef = useRef(initialFocus);
  focusRef.current = initialFocus;
  useEffect(() => {
    if (!ready) return;
    const panel = panelRef.current;
    if (!panel) return;
    const target = focusRef.current?.() ?? panel.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus({ preventScroll: true });
    // Only on landing, not on every re-measure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  if (!ready || typeof document === "undefined") return null;

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    if (tabCloses) {
      e.preventDefault();
      onClose();
      return;
    }
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const common = {
    ref: panelRef,
    role,
    // Lets a host popover that closes on outside presses (the trip planner's
    // time editor) tell a press on this portalled panel from a press outside.
    "data-mj-pw-popover": "",
    "aria-labelledby": labelledBy,
    dir: anchor.rtl ? "rtl" : "ltr",
    onKeyDown,
    className: "font-sans",
  } as const;

  if (narrow) {
    return createPortal(
      <>
        <div
          aria-hidden="true"
          data-mj-pw-popover=""
          onClick={() => onClose(false)}
          style={{ position: "fixed", inset: 0, zIndex: Z, background: "rgba(17,14,57,0.34)" }}
        />
        <div
          {...common}
          style={{
            position: "fixed",
            insetInline: 8,
            bottom: 8,
            zIndex: Z + 1,
            maxHeight: "min(80vh, 560px)",
            display: "flex",
            flexDirection: "column",
            color: PW.navy,
            ...pewterPanel(24, { shadow: "0 -12px 40px rgba(17,14,57,0.22)" }),
          }}
        >
          <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
            <p className="m-0 text-[15px] font-bold">{title}</p>
            <button
              type="button"
              onClick={() => onClose()}
              aria-label={t("common.close")}
              className="grid h-8 w-8 place-items-center rounded-full outline-none focus-visible:shadow-[0_0_0_2px_#110E39]"
              style={{ background: PW.softTint, color: PW.navy }}
            >
              <Icon name="ri-close-line" aria-hidden="true" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </div>
      </>,
      document.body,
    );
  }

  const position: CSSProperties =
    anchor.top !== undefined ? { top: anchor.top } : { bottom: anchor.bottom };
  return createPortal(
    <div
      {...common}
      style={{
        position: "fixed",
        left: anchor.left,
        width: anchor.width,
        ...position,
        zIndex: Z,
        maxHeight: anchor.maxHeight,
        overflowY: "auto",
        color: PW.navy,
        ...pewterPanel(18, { shadow: "0 18px 44px rgba(17,14,57,0.22)" }),
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Trigger                                                             */
/* ------------------------------------------------------------------ */

/** What the form passes through to the trigger: its hooks for validation and focus. */
export interface PickerFieldProps {
  readonly "data-field"?: string;
  readonly "aria-invalid"?: true;
  readonly "aria-describedby"?: string;
}

interface TriggerProps {
  readonly id: string;
  readonly labelId: string;
  /** Remix icon name, or null for none. */
  readonly icon: string | null;
  readonly text: string;
  readonly placeholder: boolean;
  readonly open: boolean;
  readonly popup: "listbox" | "dialog";
  readonly onClick: () => void;
  readonly fieldProps?: PickerFieldProps;
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
}

/**
 * A `.mj-pw-field` button. Named by the visible label and its own text
 * together ("Date, Fri 2 Oct"), since a label beside a button is not read out
 * with it.
 */
function Trigger({ id, labelId, icon, text, placeholder, open, popup, onClick, fieldProps, buttonRef }: TriggerProps) {
  return (
    <button
      ref={buttonRef}
      id={id}
      type="button"
      aria-haspopup={popup}
      aria-expanded={open}
      aria-labelledby={`${labelId} ${id}`}
      onClick={onClick}
      {...fieldProps}
      className="mj-pw-field flex min-h-11 py-2 w-full min-w-0 items-center gap-2 px-3 text-start text-[16px] outline-none"
      style={{ borderRadius: 14, color: placeholder ? PW.muted : PW.navy }}
    >
      {icon ? (
        <Icon name={icon} className="shrink-0 text-[16px]" style={{ color: PW.muted }} aria-hidden="true" />
      ) : null}
      <span className="min-w-0 flex-1 break-words">{text}</span>
      <Icon
        name={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
        className="shrink-0 text-[16px]"
        style={{ color: PW.muted }}
        aria-hidden="true"
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Select                                                              */
/* ------------------------------------------------------------------ */

export interface PewterSelectOption {
  readonly value: string;
  readonly label: string;
  /**
   * Optional heading the option sits under. Options sharing a group must be
   * next to each other; each run gets one heading (not an option, so the
   * arrow keys skip it).
   */
  readonly group?: string;
}

/** Consecutive runs of options by `group`, each option keeping its index. */
function groupRuns(options: readonly PewterSelectOption[]) {
  const runs: Array<{ group: string | undefined; items: Array<{ option: PewterSelectOption; index: number }> }> = [];
  options.forEach((option, index) => {
    const last = runs[runs.length - 1];
    if (last && last.group === option.group) last.items.push({ option, index });
    else runs.push({ group: option.group, items: [{ option, index }] });
  });
  return runs;
}

/**
 * A single-choice dropdown. The panel is a listbox: arrow keys, Home and End
 * move between rows, Enter or Space chooses, Tab and Escape close.
 */
export function PewterSelect({
  id: idProp,
  labelId,
  title,
  value,
  options,
  onChange,
  icon = "ri-route-line",
  fieldProps,
}: {
  /** Id for the trigger button, so a `<label htmlFor>` can point at it. */
  readonly id?: string;
  /** Id of the visible label above the field. */
  readonly labelId: string;
  /** The label's text, for the phone sheet's heading. */
  readonly title: string;
  readonly value: string;
  readonly options: readonly PewterSelectOption[];
  readonly onChange: (value: string) => void;
  /** Remix icon at the trigger's start, or null for none. */
  readonly icon?: string | null;
  readonly fieldProps?: PickerFieldProps;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const pop = usePopover();
  const chosen = options.find((o) => o.value === value);
  const runs = groupRuns(options);

  function rows(): HTMLElement[] {
    return Array.from(pop.panelRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>, index: number) {
    const all = rows();
    let next: number | null = null;
    if (e.key === "ArrowDown") next = Math.min(all.length - 1, index + 1);
    else if (e.key === "ArrowUp") next = Math.max(0, index - 1);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = all.length - 1;
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(options[index]!.value);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    all[next]?.focus();
  }

  function pick(next: string) {
    if (next !== value) onChange(next);
    pop.close();
  }

  return (
    <div>
      <Trigger
        id={id}
        labelId={labelId}
        icon={icon}
        text={chosen?.label ?? ""}
        placeholder={false}
        open={pop.open}
        popup="listbox"
        onClick={() => pop.setOpen((o) => !o)}
        fieldProps={fieldProps}
        buttonRef={pop.triggerRef}
      />
      <PopoverPanel
        open={pop.open}
        triggerRef={pop.triggerRef}
        panelRef={pop.panelRef}
        onClose={pop.close}
        title={title}
        labelledBy={labelId}
        role="listbox"
        width="trigger"
        estHeight={Math.min(options.length * 46 + runs.filter((r) => r.group).length * 30 + 12, 360)}
        tabCloses
        initialFocus={() =>
          pop.panelRef.current?.querySelector<HTMLElement>('[aria-selected="true"]') ?? null
        }
      >
        <div className="p-1.5" style={{ maxHeight: 360 }}>
          {runs.map((run, r) => {
            const rowNodes = run.items.map(({ option: o, index: i }) => {
              const selected = o.value === value;
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={selected}
                  tabIndex={selected || (!chosen && i === 0) ? 0 : -1}
                  onClick={() => pick(o.value)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-start text-[15px] outline-none hover:bg-[rgba(17,14,57,0.05)] focus-visible:shadow-[inset_0_0_0_2px_#110E39]"
                  style={selected ? { ...pewterInset, fontWeight: 600 } : undefined}
                >
                  <span className="min-w-0 flex-1">{o.label}</span>
                  {selected ? <Icon name="ri-check-line" className="shrink-0 text-[16px]" aria-hidden="true" /> : null}
                </div>
              );
            });
            if (!run.group) return <Fragment key={`run-${r}`}>{rowNodes}</Fragment>;
            const headingId = `${id}-group-${r}`;
            return (
              <div key={`run-${r}`} role="group" aria-labelledby={headingId}>
                <div
                  id={headingId}
                  className="px-3 pb-1 pt-2.5 text-start text-[11px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: PW.muted }}
                >
                  {run.group}
                </div>
                {rowNodes}
              </div>
            );
          })}
        </div>
      </PopoverPanel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Date                                                                */
/* ------------------------------------------------------------------ */

/**
 * "Fri 2 Oct" in the page's language, Latin digits in every locale. No year:
 * the field is half a phone wide, and a ride is booked weeks ahead, not years.
 */
export function formatPickerDate(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(`${normalizeLocale(lang)}-u-nu-latn`, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${iso}T00:00:00Z`));
}

/**
 * A date field over the booking calendar. Days before `minDate` are disabled
 * and there is no text entry, so a past date cannot be chosen at all. Picking
 * a day closes the panel.
 */
export function PewterDateField({
  id: idProp,
  labelId,
  title,
  value,
  onChange,
  minDate,
  openOn,
  lang,
  placeholder,
  fieldProps,
}: {
  /** Id for the trigger button, so a `<label htmlFor>` can point at it. */
  readonly id?: string;
  readonly labelId: string;
  readonly title: string;
  /** YYYY-MM-DD, or "" for none. */
  readonly value: string;
  readonly onChange: (iso: string) => void;
  /** YYYY-MM-DD. The calendar already strikes out days before Casablanca's today. */
  readonly minDate?: string;
  /** YYYY-MM-DD. The month the calendar opens on while no date is chosen. */
  readonly openOn?: string;
  readonly lang: string;
  readonly placeholder: string;
  readonly fieldProps?: PickerFieldProps;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const pop = usePopover();

  return (
    <div>
      <Trigger
        id={id}
        labelId={labelId}
        icon="ri-calendar-line"
        text={value ? formatPickerDate(value, lang) : placeholder}
        placeholder={!value}
        open={pop.open}
        popup="dialog"
        onClick={() => pop.setOpen((o) => !o)}
        fieldProps={fieldProps}
        buttonRef={pop.triggerRef}
      />
      <PopoverPanel
        open={pop.open}
        triggerRef={pop.triggerRef}
        panelRef={pop.panelRef}
        onClose={pop.close}
        title={title}
        labelledBy={labelId}
        role="dialog"
        width={316}
        estHeight={330}
        initialFocus={() => {
          const panel = pop.panelRef.current;
          return (
            panel?.querySelector<HTMLElement>(`[data-iso="${value}"]:not([disabled])`) ??
            panel?.querySelector<HTMLElement>("[data-iso]:not([disabled])")
          );
        }}
      >
        <div className="p-3">
          <CalendarGrid
            selected={value || null}
            startAtSelected
            startAt={openOn}
            isDateDisabled={minDate ? (iso) => iso < minDate : undefined}
            onSelect={(iso) => {
              if (minDate && iso < minDate) return;
              onChange(iso);
              pop.close();
            }}
          />
        </div>
      </PopoverPanel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Time                                                                */
/* ------------------------------------------------------------------ */

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = [0, 15, 30, 45] as const;
const pad2 = (n: number) => String(n).padStart(2, "0");
const toMinutes = (h: number, m: number) => h * 60 + m;

/** "HH:MM" to minutes past midnight, or null. */
export function timeToMinutes(time: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Minutes past midnight in Casablanca now. */
export function moroccoNowMinutes(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Casablanca",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 60 + get("minute");
}

/**
 * A time field at 15-minute steps, any hour of the day: an hours column and a
 * minutes column, each scrolling on its own. Choosing an hour keeps the panel
 * open so the minutes can follow; choosing the minutes closes it.
 *
 * `notBefore` (minutes past midnight) disables every slot at or before it: the
 * form passes Casablanca's clock when the chosen date is today, because the
 * API refuses a pick-up in the past.
 *
 * `later` adds a "not decided yet" choice above the columns, for a return
 * time the guest does not know when they book.
 */
export function PewterTimeField({
  labelId,
  title,
  value,
  onChange,
  notBefore = null,
  placeholder,
  later,
  fieldProps,
}: {
  readonly labelId: string;
  readonly title: string;
  /** HH:MM, or "" for none. */
  readonly value: string;
  readonly onChange: (time: string) => void;
  readonly notBefore?: number | null;
  readonly placeholder: string;
  readonly later?: { readonly label: string; readonly chosen: boolean; readonly onChoose: () => void };
  readonly fieldProps?: PickerFieldProps;
}) {
  const { t } = useTranslation();
  const id = useId();
  const hoursId = useId();
  const minutesId = useId();
  const pop = usePopover();
  const hoursRef = useRef<HTMLDivElement | null>(null);

  const current = timeToMinutes(value);
  const hour = current === null ? null : Math.floor(current / 60);
  const minute = current === null ? null : current % 60;
  const blocked = (h: number, m: number) => notBefore !== null && toMinutes(h, m) <= notBefore;
  const hourOpen = (h: number) => MINUTES.some((m) => !blocked(h, m));
  const minuteOpen = (m: number) => (hour !== null ? !blocked(hour, m) : HOURS.some((h) => !blocked(h, m)));

  function pickHour(h: number) {
    const m = minute !== null && !blocked(h, minute) ? minute : MINUTES.find((x) => !blocked(h, x));
    if (m === undefined) return;
    onChange(`${pad2(h)}:${pad2(m)}`);
  }

  function pickMinute(m: number) {
    const h = hour !== null && !blocked(hour, m) ? hour : HOURS.find((x) => !blocked(x, m));
    if (h === undefined) return;
    onChange(`${pad2(h)}:${pad2(m)}`);
    pop.close();
  }

  /* Bring the chosen hour (or the first one still open) into the middle of
     its column once the panel lands. Moves the column's own scrollTop: a
     scrollIntoView would scroll the page too. */
  useLayoutEffect(() => {
    if (!pop.open) return;
    const frame = requestAnimationFrame(() => {
      const col = hoursRef.current;
      const target =
        col?.querySelector<HTMLElement>('[aria-pressed="true"]') ??
        col?.querySelector<HTMLElement>("button:not([disabled])");
      if (col && target) col.scrollTop = target.offsetTop - col.clientHeight / 2 + target.offsetHeight / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [pop.open]);

  const chip =
    "mj-pw-chip h-10 w-full shrink-0 text-[15px] font-semibold tabular-nums outline-none focus-visible:shadow-[0_0_0_2px_#110E39] disabled:cursor-not-allowed disabled:line-through disabled:opacity-35";
  const column = "relative flex max-h-[232px] flex-col gap-1.5 overflow-y-auto p-1 [scrollbar-width:thin]";

  const text = later?.chosen ? later.label : value || placeholder;

  return (
    <div>
      <Trigger
        id={id}
        labelId={labelId}
        icon="ri-time-line"
        text={text}
        placeholder={!value && !later?.chosen}
        open={pop.open}
        popup="dialog"
        onClick={() => pop.setOpen((o) => !o)}
        fieldProps={fieldProps}
        buttonRef={pop.triggerRef}
      />
      <PopoverPanel
        open={pop.open}
        triggerRef={pop.triggerRef}
        panelRef={pop.panelRef}
        onClose={pop.close}
        title={title}
        labelledBy={labelId}
        role="dialog"
        width={256}
        estHeight={later ? 340 : 290}
        initialFocus={() =>
          hoursRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]') ??
          hoursRef.current?.querySelector<HTMLElement>("button:not([disabled])")
        }
      >
        <div className="p-2.5">
          {later ? (
            <button
              type="button"
              aria-pressed={later.chosen}
              onClick={() => {
                later.onChoose();
                pop.close();
              }}
              className={`${chip} mb-2.5 px-3`}
            >
              {later.label}
            </button>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <div role="group" aria-labelledby={hoursId}>
              <p id={hoursId} className="mb-1 px-1 text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: PW.muted }}>
                {t("transport.hours")}
              </p>
              <div ref={hoursRef} className={column}>
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    aria-pressed={hour === h && !later?.chosen}
                    disabled={!hourOpen(h)}
                    onClick={() => pickHour(h)}
                    className={chip}
                  >
                    {pad2(h)}
                  </button>
                ))}
              </div>
            </div>
            <div role="group" aria-labelledby={minutesId}>
              <p id={minutesId} className="mb-1 px-1 text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: PW.muted }}>
                {t("transport.minutes")}
              </p>
              <div className={column}>
                {MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-label={hour !== null ? `${pad2(hour)}:${pad2(m)}` : undefined}
                    aria-pressed={minute === m && !later?.chosen}
                    disabled={!minuteOpen(m)}
                    onClick={() => pickMinute(m)}
                    className={chip}
                  >
                    {pad2(m)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PopoverPanel>
    </div>
  );
}
